// @ts-check
// =============================================================================
// js/screens/duel/bars.js — the two resource bars (PIXI).
//
// WHAT THIS IS
//   The port of the old top/bottom player panels (RenderGameText :3525–3658):
//   one button row per player: [ticks] [deck "hand/deck"] [PORTRAIT] [crop]
//   [meat], each side justified space-evenly between the frame strip's edge
//   and the portrait. (The old hero-name label and the separate hand chip are
//   gone — the portrait and the combined deck counter do those jobs better.)
//   YOUR bar's deck/crop/meat
//   are the Draw / Harvest / Devour buttons; the opponent's are display-only.
//   The leader PORTRAIT in the middle IS the face target (replaces the old
//   "ATTACK OPPONENT"/"PLAY ON YOURSELF" pills): it highlights exactly like a
//   card tile when it's actionable, tap targets it, press-hold shows the full
//   leader card. Mulligan and the devour-win hint live here too.
//
// PSEUDO-REACT RENDERING
//   mountBars()          builds everything ONCE (per mount/layout) and keeps
//                        REFS to every dynamic field.
//   updateBars()         each new state just sets .text/.texture/.visible on
//                        those refs — counters tick over without the bar ever
//                        being torn down.
//   applyBarTargeting()  FEEDBACK pass — portrait + Devour rings, in place.
// =============================================================================

import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { store } from '../../core/store.js';
import { click, highlightFor } from './targeting.js';
import { HIGHLIGHT_COLORS, gentlePulse, killPulse } from './board.js';
import { applyHighlight } from './highlight-fx.js';
import { HERO_ASSET_NAMES } from '../../ui/deck-list.js';

/**
 * Entry point: mount once, then field-update forever (until duel.js clears
 * scene.barRefs on layout/duel change).
 * @param {object} scene  The duel scene.
 * @param {any} duel      Current duel snapshot (player1 = viewer side).
 * @returns {Promise<void>}
 */
export async function renderBars(scene, duel) {
	if (!scene.barRefs) {
		if (!scene.barsMounting) {
			scene.barsMounting = mountBars(scene, duel).finally(() => {
				scene.barsMounting = null;
			});
		}
		await scene.barsMounting;
	}
	updateBars(scene, duel);
	applyBarTargeting(scene);
}

/**
 * FULL build — creates every widget and records refs to the dynamic fields.
 * @param {object} scene
 * @param {any} duel
 */
async function mountBars(scene, duel) {
	const topLayer = scene.layers.topBar;
	const botLayer = scene.layers.bottomBar;
	topLayer.removeChildren();
	botLayer.removeChildren();

	const { width, topBarH, botBarY, botBarH, topBarY = 0 } = scene.metrics;
	const interactive = !duel.spectating && typeof duel.player1.hand !== 'number';

	const refs = { top: null, bottom: null, portraits: {}, devourChip: null, mulligan: null, spectators: null, devourFlash: null, faceDevourFlash: null };

	// ---- Top: the opponent (display-only counters + the portrait target) ----
	refs.top = await buildBar(scene, topLayer, {
		heroIndex: duel.player2.hero,
		y: topBarY + 4,
		h: topBarH - 8,
		width,
		mirrored: true,
		interactive: false,
		refs,
	});

	// Spectator count rides the top bar (old :3700), clear of the log toggle.
	const spectators = new PIXI.Text({
		text: '',
		style: { fontFamily: 'system-ui', fontSize: 12, fill: 0x9c93b0 },
	});
	spectators.y = topBarY + 6;
	spectators.visible = false;
	topLayer.addChild(spectators);
	refs.spectators = spectators;

	// ---- Bottom: you (interactive buttons) ---------------------------------
	refs.bottom = await buildBar(scene, botLayer, {
		heroIndex: duel.player1.hero,
		y: botBarY + 4,
		h: botBarH - 8,
		width,
		mirrored: false,
		interactive,
		refs,
	});

	// Mulligan (visibility state-driven — see updateBars). Centered in the
	// band between your row and the hand — parked at the row's end it clipped
	// off the right edge of phone screens.
	refs.mulligan = textButton('MULLIGAN', width / 2, botBarY + botBarH + 2, () => click('Mulligan'));
	refs.mulligan.visible = false;
	botLayer.addChild(refs.mulligan);

	// (The old "← Devour Your Opponent!" text cue is gone — the win is now led
	// by the Devour button flashing red; see applyBarTargeting.)

	refs.interactive = interactive;
	scene.barRefs = refs;
}

/**
 * Field-level update: pour the new state into the mounted widgets.
 * @param {object} scene
 * @param {any} duel
 */
function updateBars(scene, duel) {
	const refs = scene.barRefs;
	if (!refs) return;
	// A DEFEATED player's numbers all go flat gray — no ramps, no flashing,
	// no celebration effects. They lost; the bar goes quiet.
	updateBarSide(refs.top, duel.player2, !!duel.player1.winner);
	updateBarSide(refs.bottom, duel.player1, !!duel.player2.winner);

	refs.mulligan.visible = !!(refs.interactive && duel.player1.tick
		&& duel.player1.actionsSpent === 0 && !duel.player1.tookMulligan);

	const spectatorCount = duel.spectatorCount || 0;
	refs.spectators.visible = spectatorCount > 0;
	if (spectatorCount > 0) {
		refs.spectators.text = `Judgy Spectators: ${spectatorCount}`;
		refs.spectators.x = scene.metrics.width - refs.spectators.width - 60;
	}
}

/**
 * Update one side's clock + counters in place.
 * @param {any} side    The refs from buildBar.
 * @param {any} player  duel.player1 / duel.player2.
 * @param {boolean} [defeated]  This player lost: numbers flat gray, no effects.
 */
function updateBarSide(side, player, defeated = false) {
	if (!side) return;
	const tick = Math.min(8, player.tick);

	// Tick clock: every change PULSES; a refill (turn start) steps up ONE
	// TICK AT A TIME, pulsing each step — that's the turn-handoff cue.
	if (side.clock && tick !== side.tick) {
		const from = side.tick;
		side.tick = tick;
		side.tickAnim?.kill();
		if (from == null) {
			setClockFrame(side.clock, tick); // first paint: just show it
		} else {
			const steps = tick > from
				? Array.from({ length: tick - from }, (_, i) => from + 1 + i)
				: [tick];
			const timeline = gsap.timeline();
			for (const frame of steps) {
				timeline.call(() => setClockFrame(side.clock, frame));
				timeline.add(clockPulse(side.clock));
			}
			side.tickAnim = timeline;
		}
	}
	if (side.clock) side.clock.alpha = player.tick === 0 ? 0.45 : 1;
	updateTickCount(side, player.tick, defeated);

	const handCount = typeof player.hand === 'number' ? player.hand : player.hand.length;
	// The deck button reads "hand/deck" (e.g. "5/25"); the DECK number runs
	// the low-deck color ramp and flashes at zero, the HAND number runs the
	// fullness color (styleHandCount), and the slash BETWEEN them is its own
	// text node precisely so neither number's color bleeds into it.
	side.counts.deckHand.text = String(handCount);
	styleHandCount(side, player, handCount);
	side.counts.deck.text = String(player.deck);
	const total = side.counts.deckHand.width + side.counts.deckSlash.width + side.counts.deck.width;
	side.counts.deckHand.x = -total / 2;
	side.counts.deckSlash.x = side.counts.deckHand.x + side.counts.deckHand.width;
	side.counts.deck.x = side.counts.deckSlash.x + side.counts.deckSlash.width;
	side.counts.crop.text = String(player.crop);
	side.counts.meat.text = String(player.meat);

	if (defeated) {
		// Flat gray across the board — kill the zero flash and any ramps.
		if (side.deckFlash) {
			side.deckFlash.kill();
			side.deckFlash = null;
		}
		for (const key of ['deckHand', 'deckSlash', 'deck', 'crop', 'meat']) {
			const text = side.counts[key];
			text.__flashTimer?.kill();
			gsap.killTweensOf(text);
			text.style.fill = 0x8f8a99;
		}
		side.prev = { crop: player.crop, meat: player.meat, hand: handCount, deck: player.deck };
		return;
	}
	styleDeckCount(side, player.deck);

	// Change feedback: bounce the chip + float the delta + flash the number.
	if (side.prev) {
		for (const name of ['crop', 'meat']) {
			const delta = player[name] - side.prev[name];
			if (delta) announceResourceChange(side, name, delta);
		}
		// Drawing a card: same treatment on the deck chip, keyed off the HAND
		// count going up — its number flashes green.
		const drawn = handCount - side.prev.hand;
		if (drawn > 0) {
			announceDraw(side, drawn);
		}
		// Deck CHANGE beyond draws: draws already floated green off the hand
		// count above, so credit them back before floating what remains on the
		// DECK itself — red for bitten cards (face attacks, Chomp, DeckBite
		// victims), green for additions (Shuffle copies, DeckBite loot, the
		// end-of-turn hand trim returning extras). The credit is capped by the
		// actual deck DECREASE so hand gains from thin air (GetCopy/NewCard
		// effects) can't fake a green deck gain.
		const deckLoss = Math.max(0, side.prev.deck - player.deck);
		const fromDeck = Math.min(Math.max(0, drawn), deckLoss);
		const deckDelta = (player.deck - side.prev.deck) + fromDeck;
		if (deckDelta) {
			announceDeckChange(side, deckDelta);
		}
	}
	side.prev = { crop: player.crop, meat: player.meat, hand: handCount, deck: player.deck };
}

/**
 * The surplus tick counter's hue: 5 ticks = yellow (60), and each further
 * tick steps −20 — so every THREE ticks moves −60 along the color wheel
 * (8 = red / hue 0), wrapping and looping forever however high the pile
 * grows (11 = 300, 14 = 240, … 23 = back to yellow).
 * @param {number} ticks
 * @returns {number} 0–359
 */
export function tickCountHue(ticks) {
	return ((60 - (ticks - 5) * 20) % 360 + 360) % 360;
}

/**
 * Show the REAL tick count over a side's clock at 5+ ticks (the dial's
 * frames cap at 8), colored by tickCountHue. Hidden below 5.
 * @param {any} side
 * @param {number} ticks        The player's true (uncapped) tick count.
 * @param {boolean} [defeated]  Flat gray, matching the rest of a loser's bar.
 */
function updateTickCount(side, ticks, defeated = false) {
	const label = side.tickCount;
	if (!label || label.destroyed) return;
	side.realTick = ticks;
	label.visible = ticks >= 5;
	if (!label.visible) return;
	label.text = String(ticks);
	label.style.fill = defeated ? 0x8f8a99 : `hsl(${tickCountHue(ticks)}, 100%, 55%)`;
}

/**
 * Flash a counter text a color, then settle back to its resting fill.
 * @param {any} text     A PIXI.Text.
 * @param {number} color Flash color.
 * @param {number} rest  The fill to settle back to.
 */
function flashCount(text, color, rest) {
	if (!text || text.destroyed) return;
	text.__flashTimer?.kill(); // retrigger cleanly
	text.style.fill = color;
	text.__flashTimer = gsap.delayedCall(0.55, () => {
		if (!text.destroyed) text.style.fill = rest;
	});
}

/**
 * Draw feedback on the deck chip: bounce, float "+N", flash the HAND count
 * green — the same language as the harvest/devour resource effects.
 * @param {any} side
 * @param {number} drawn
 */
function announceDraw(side, drawn) {
	const chip = side.chips?.deck;
	if (!chip || chip.container.destroyed) return;
	gsap.timeline()
		.to(chip.container.scale, { x: 1.18, y: 1.18, duration: 0.1, ease: 'none' })
		.to(chip.container.scale, { x: 1, y: 1, duration: 0.22, ease: 'power2.out' });
	floatDelta(side, chip, drawn, 0x9dff9d);
	// The flash settles back to the fullness color, not plain white.
	flashCount(side.counts.deckHand, 0x9dff9d, side.handRest ?? 0xffffff);
}

/**
 * Deck-change feedback: bounce the deck chip and float the delta — red "−N"
 * for bitten cards, green "+N" for cards added to the deck. The deck
 * NUMBER's fill is deliberately left alone — it belongs to styleDeckCount's
 * low-deck warning ramp (and the zero flash), which a flash-and-settle
 * would fight.
 * @param {any} side
 * @param {number} delta  Signed change beyond draws (negative = bitten).
 */
function announceDeckChange(side, delta) {
	const chip = side.chips?.deck;
	if (!chip || chip.container.destroyed) return;
	gsap.timeline()
		.to(chip.container.scale, { x: 1.18, y: 1.18, duration: 0.1, ease: 'none' })
		.to(chip.container.scale, { x: 1, y: 1, duration: 0.22, ease: 'power2.out' });
	floatDelta(side, chip, delta, delta > 0 ? 0x9dff9d : 0xff9d9d);
}

/**
 * Optimistic tick spend: the instant the player SENDS an action, the clock
 * steps down one — the cost reads as paid before the server answers. The
 * side's tracked tick moves with it, so the confirming update sees no
 * change; a state that GRANTS ticks then steps back UP with pulses (down,
 * then up — exactly the payment-then-refund read).
 * @param {object} scene
 * @returns {void}
 */
export function previewTickSpend(scene) {
	const side = scene?.barRefs?.bottom;
	if (!side || !side.clock || side.tick == null || side.tick <= 0) return;
	side.tick -= 1;
	side.tickAnim?.kill();
	side.tickAnim = gsap.timeline()
		.call(() => setClockFrame(side.clock, side.tick))
		.add(clockPulse(side.clock));
	side.clock.alpha = side.tick === 0 ? 0.45 : 1;
	// The surplus counter spends along with the dial (realTick holds the
	// TRUE count — side.tick is capped at the dial's 8 frames).
	if (side.realTick != null && side.realTick > 0) {
		updateTickCount(side, side.realTick - 1);
	}
}

/* Hand-fullness colors. Deliberately NOT the deck's warm yellow→red ramp —
   the two numbers sit side by side ("hand/deck") and must never look like
   the same warning. The hand runs the COOL lane: slightly grey below full,
   PURE WHITE exactly at hand size, ice blue on the first card over, then
   blending to a hot magenta = "bursting" at double hand size. */
const HAND_UNDER_COLOR = 0xd6d2de;
const HAND_FULL_COLOR = 0x86d9ff;
const HAND_OVER_COLOR = 0xff4fd8;

/**
 * A player's maximum hand size, mirroring the engine's CalculateHandsize
 * (src/engine/turn.js): base 5, plus any ongoing "Handsize" modifier,
 * −2 for hero 9 (the small-handed bird).
 * @param {any} player
 * @returns {number}
 */
function maxHandSize(player) {
	let size = 5 + (player.static?.['Handsize'] || 0);
	if (player.hero == 9) {
		size -= 2;
	}
	return Math.max(1, size);
}

/**
 * Color the HAND number by fullness: slightly grey under the limit, pure
 * white exactly AT it, ice blue on the first card over, then blending toward
 * hot magenta as the hand approaches DOUBLE its size (clamped there). The
 * draw flash settles back to this color (side.handRest).
 * @param {any} side
 * @param {any} player
 * @param {number} handCount
 */
function styleHandCount(side, player, handCount) {
	const text = side.counts.deckHand;
	if (!text || text.destroyed) return;
	const limit = maxHandSize(player);
	let fill = HAND_UNDER_COLOR;
	if (handCount >= limit * 2) {
		fill = HAND_OVER_COLOR;
	} else if (handCount > limit) {
		// limit+1 = exactly ice blue; double = exactly magenta (guarded for
		// a degenerate limit of 1, where only the clamp above can apply).
		const t = (handCount - limit - 1) / Math.max(1, limit - 1);
		fill = lerpColor(HAND_FULL_COLOR, HAND_OVER_COLOR, t);
	} else if (handCount === limit) {
		fill = 0xffffff;
	}
	side.handRest = fill;
	text.__flashTimer?.kill(); // a stale draw-flash must not settle to an old color
	text.style.fill = fill;
}

/**
 * Blend two 0xRRGGBB colors.
 * @param {number} a
 * @param {number} b
 * @param {number} t  0 (a) .. 1 (b)
 * @returns {number}
 */
function lerpColor(a, b, t) {
	const mix = (shift) => {
		const av = (a >> shift) & 0xff;
		const bv = (b >> shift) & 0xff;
		return Math.round(av + (bv - av) * t) << shift;
	};
	return mix(16) | mix(8) | mix(0);
}

/**
 * The low-deck warning ramp for the deck number:
 *   15+ .. white · 15→10 fades to yellow · 10→5 fades to orange ·
 *   under 5 SNAPS to red · at 0 it flashes red/white.
 * @param {any} side  A buildBar refs bundle.
 * @param {number} deck
 */
function styleDeckCount(side, deck) {
	const text = side.counts.deck;
	if (!text || text.destroyed) return;

	if (deck === 0) {
		if (!side.deckFlash) {
			side.deckFlash = gsap.timeline({ repeat: -1 })
				.call(() => { if (!text.destroyed) text.style.fill = 0xff4040; })
				.to({}, { duration: 0.66 })
				.call(() => { if (!text.destroyed) text.style.fill = 0xffffff; })
				.to({}, { duration: 0.66 });
		}
		return;
	}
	if (side.deckFlash) {
		side.deckFlash.kill();
		side.deckFlash = null;
	}
	let color = 0xffffff;
	if (deck < 5) {
		color = 0xff4040; // sudden red
	} else if (deck < 10) {
		color = lerpColor(0xffe14d, 0xff9b2f, (10 - deck) / 5); // yellow -> orange
	} else if (deck < 15) {
		color = lerpColor(0xffffff, 0xffe14d, (15 - deck) / 5); // white -> yellow
	}
	text.style.fill = color;
}

/**
 * Swap the clock sprite to a tick frame (textures preloaded at build).
 * @param {any} clock
 * @param {number} frame
 */
function setClockFrame(clock, frame) {
	if (clock.destroyed) return;
	const texture = PIXI.Assets.get(`/gui/clock/${Math.max(0, Math.min(8, frame))}-4.png`);
	if (texture) clock.texture = texture;
}

/**
 * One scale pulse of the (center-anchored, x-mirrored) clock sprite.
 * @param {any} clock
 * @returns {gsap.core.Timeline}
 */
function clockPulse(clock) {
	const base = clock.__baseScale;
	return gsap.timeline()
		.to(clock.scale, { x: base.x * 1.3, y: base.y * 1.3, duration: 0.09, ease: 'none' })
		.to(clock.scale, { x: base.x, y: base.y, duration: 0.12, ease: 'power1.out' });
}

/**
 * Bounce a resource chip, float its "+N"/"−N" delta, and flash the number
 * (green on gain, red on spend).
 * @param {any} side   A buildBar refs bundle (has .chips and .layer).
 * @param {string} name  'crop' | 'meat'
 * @param {number} delta
 */
function announceResourceChange(side, name, delta) {
	const chip = side.chips?.[name];
	if (!chip || chip.container.destroyed) return;

	gsap.timeline()
		.to(chip.container.scale, { x: 1.18, y: 1.18, duration: 0.1, ease: 'none' })
		.to(chip.container.scale, { x: 1, y: 1, duration: 0.22, ease: 'power2.out' });
	floatDelta(side, chip, delta, delta > 0 ? 0x9dff9d : 0xff9d9d);
	flashCount(side.counts[name], delta > 0 ? 0x9dff9d : 0xff9d9d, 0xffffff);
}

/**
 * The floating "+N"/"−N" above a chip (shared by resources and draws).
 * @param {any} side
 * @param {any} chip   A chipRefs entry ({container, w, h}).
 * @param {number} delta
 * @param {number} color
 */
function floatDelta(side, chip, delta, color) {
	const float = new PIXI.Text({
		text: (delta > 0 ? '+' : '−') + Math.abs(delta),
		style: {
			fontFamily: 'Luckiest Guy', fontSize: Math.max(15, chip.h * 0.55),
			fill: color,
			stroke: { color: 0x000000, width: 3 },
		},
	});
	float.anchor.set(0.5);
	float.x = chip.container.x;
	float.y = chip.container.y - chip.h * 0.7;
	side.layer.addChild(float);
	gsap.timeline()
		.to(float, { y: float.y - chip.h * 0.9, duration: 0.9, ease: 'power1.out' }, 0)
		.to(float, { alpha: 0, duration: 0.35, ease: 'none' }, 0.55)
		.call(() => float.destroy());
}

/**
 * FEEDBACK pass: restyle the portrait rings and the Devour chip ring for the
 * current targeting state. Mutates in place — no rebuilds.
 * @param {object} scene
 * @returns {void}
 */
export function applyBarTargeting(scene) {
	const refs = scene.barRefs;
	if (!refs) return;
	const duel = store.duel;
	// The always-legal buttons (Draw/Harvest) show clickable ONLY on our
	// live turn — on the enemy's turn every highlight disappears.
	const myTurn = !!(refs.interactive && duel && !duel.GameOver
		&& typeof duel.player1.hand !== 'number' && duel.player1.tick);

	// Devour selection state, needed by both the face target below and the
	// Devour button further down.
	const devourHighlight = highlightFor('Devour');
	// The enemy is one bite from being eaten (deck empty) and you've picked
	// Devour, so their portrait is now the live target. GetTargets only offers
	// "Opponent" for a Devour when player2.deck === 0, so this is exactly the
	// finishing move.
	const eatingEnemy = !!(myTurn && duel.player2?.deck === 0 && devourHighlight === 'selected');

	// The leader portraits are the face targets — v2 outline grammar:
	// green = selected, orange = a legal target. The FACES themselves never
	// darken or gray — the leaders stay full color at all times.
	for (const spot of ['Opponent', 'Yourself']) {
		const portrait = refs.portraits[spot];
		if (!portrait) continue;
		const highlight = highlightFor(spot);
		const outline = highlight && highlight !== 'pickable' ? highlight : null;

		// When the enemy is the devour target, flash their outline normal<->red
		// on the SAME 0.66s cadence as the Devour button + the deck-0 number,
		// leading the player to land the finishing tap.
		const flashEnemy = spot === 'Opponent' && eatingEnemy && outline === 'targeted';
		if (flashEnemy && !portrait.container.destroyed) {
			if (!refs.faceDevourFlash) {
				refs.faceDevourFlash = gsap.timeline({ repeat: -1 })
					.call(() => applyHighlight(portrait.container, 'devourwin'))
					.to({}, { duration: 0.66 })
					.call(() => applyHighlight(portrait.container, 'targeted'))
					.to({}, { duration: 0.66 });
				// Blink IN PHASE with the enemy's 0-cards-in-deck number flash so
				// the red leader highlight and the red deck number pulse together.
				syncFlash(refs.faceDevourFlash, refs.top?.deckFlash);
			}
		} else {
			if (spot === 'Opponent' && refs.faceDevourFlash) {
				refs.faceDevourFlash.kill();
				refs.faceDevourFlash = null;
			}
			applyHighlight(portrait.container, outline);
		}
		if (portrait.sprite) {
			portrait.sprite.filters = null;
			portrait.sprite.alpha = 1;
		}
		/* v1 RING DRAWING — replaced by the outline filters above:
		killPulse(portrait.ring);
		gsap.killTweensOf(portrait.ring);
		portrait.ring.clear();
		portrait.ring.alpha = 1;
		if (highlight && highlight !== 'pickable') {
			portrait.ring
				.circle(portrait.w / 2, portrait.h / 2, portrait.r + 2)
				.stroke({ width: 4, color: HIGHLIGHT_COLORS[highlight] });
			if (highlight === 'targeted') {
				gentlePulse(portrait.ring);
			}
		}
		*/
	}

	// YOUR action buttons: teal while clickable, green while the Devour
	// selection is live. (Draw/Harvest are always-legal actions, so their
	// clickability is the turn itself + a deck to draw from.)
	const chips = refs.bottom?.chips || {};
	applyHighlight(chips.deck?.container, myTurn && duel.player1.deck > 0 ? 'pickable' : null);
	applyHighlight(chips.crop?.container, myTurn ? 'pickable' : null);

	// The Devour button. When the OPPONENT'S DECK IS EMPTY the win is one Devour
	// away — flash its border red<->teal on the SAME 0.66s cadence as the deck-0
	// number flash, pulling the player toward the finishing move. Otherwise it
	// follows the normal selected(green)/pickable(teal) outline.
	const meat = chips.meat?.container;
	const devourWin = !!(myTurn && duel.player2?.deck === 0 && devourHighlight === 'pickable');
	if (devourWin && meat && !meat.destroyed) {
		if (!refs.devourFlash) {
			refs.devourFlash = gsap.timeline({ repeat: -1 })
				.call(() => applyHighlight(meat, 'devourwin'))
				.to({}, { duration: 0.66 })
				.call(() => applyHighlight(meat, 'pickable'))
				.to({}, { duration: 0.66 });
			syncFlash(refs.devourFlash, refs.top?.deckFlash); // in phase with the deck-0 number
		}
	} else {
		if (refs.devourFlash) {
			refs.devourFlash.kill();
			refs.devourFlash = null;
		}
		applyHighlight(
			meat,
			devourHighlight === 'selected' ? 'selected'
				: myTurn && devourHighlight === 'pickable' ? 'pickable' : null,
		);
	}

	/* v1 DEVOUR-CHIP RING — replaced by the outline filters above:
	const chip = refs.devourChip;
	if (chip) {
		const highlight = highlightFor('Devour');
		killPulse(chip.ring);
		gsap.killTweensOf(chip.ring);
		chip.ring.clear();
		chip.ring.alpha = 1;
		if (highlight && highlight !== 'pickable') {
			chip.ring
				.roundRect(-3, -3, chip.w + 6, chip.h + 6, 8)
				.stroke({ width: 3, color: HIGHLIGHT_COLORS[highlight] });
			if (highlight === 'targeted') {
				gentlePulse(chip.ring);
			}
		}
	}
	*/

	// The MULLIGAN button is a real action too — outline it teal (pickable) while
	// it's offered, so it reads as clickable like the resource buttons. (It's a
	// direct action, not a targeting source, so we drive the outline off its own
	// visibility rather than highlightFor.)
	if (refs.mulligan) {
		applyHighlight(refs.mulligan, refs.mulligan.visible ? 'pickable' : null);
	}
}

/**
 * Align a repeating flash timeline to another's phase, so the two blink
 * together (used to keep the devour cues in step with the deck-0 number).
 * @param {any} timeline  The just-created flash timeline.
 * @param {any} master    The timeline to match (no-op if absent).
 */
function syncFlash(timeline, master) {
	if (timeline && master && !master.paused?.()) {
		timeline.time(master.time());
	}
}

/** The gui/buttons canvases are 454×334 — padding is baked into the image,
 *  so the buttons may render larger than the frame strip's inner height. */
const BUTTON_AR = 454 / 334;

/** Clock height in button-height units (keeps its old relative look). */
const CLOCK_RATIO = 0.8;

/**
 * One player's row: [clock] [deck "hand/deck"] [PORTRAIT] [crop] [meat].
 * Each side's chips are JUSTIFIED space-evenly between the frame strip's
 * edge and the portrait — equal gaps to the edges and each other.
 * Values are seeded blank — updateBars pours in the numbers.
 * @param {object} scene
 * @param {PIXI.Container} layer
 * @param {{hero:any, heroIndex:number, y:number, h:number, width:number,
 *          mirrored:boolean, interactive:boolean, refs:any}} opts
 * @returns {Promise<{clock:any, counts:Record<string,any>, chips:Record<string,any>, layer:PIXI.Container}>}
 */
async function buildBar(scene, layer, { heroIndex, y, h, width, mirrored, interactive, refs }) {
	const m = scene.metrics;
	const centerX = m.boardX != null ? m.boardX + (m.tileW * 4 + m.gap * 3) / 2 : width / 2;
	const portraitD = m.portraitD || (h - 8) * 2;
	const bg = m.bg || { x: 0, w: width };
	// The justification space: from the strip's edge (inset past its border)
	// to the portrait's rim, per side.
	const pad = Math.max(8, bg.w * 0.02);
	const leftEdge = Math.max(0, bg.x) + pad;
	const rightEdge = Math.min(width, bg.x + bg.w) - pad;
	const leftAvail = centerX - portraitD / 2 - leftEdge;
	const rightAvail = rightEdge - (centerX + portraitD / 2);
	// Button height: as BIG as fits — the band, the strip (×1.6: the art's
	// padding is in the image), and both sides' widths with minimum gaps.
	const MIN_GAP = 4;
	const B = Math.max(20, Math.min(
		h * 1.2,
		(m.barIconMax || 46) * 1.6,
		78,
		(leftAvail - 3 * MIN_GAP) / (2 * BUTTON_AR),
		(rightAvail - 3 * MIN_GAP) / (2 * BUTTON_AR),
	));
	const bw = B * BUTTON_AR;
	const clockH = B * CLOCK_RATIO;
	const fontSize = Math.max(13, B * 0.34);
	const cy = y + h / 2;

	/** @type {Record<string, any>} */
	const counts = {};
	/** @type {Record<string, any>} */
	const chipRefs = {};

	/** Build one illustrated button chip with its centered counter. */
	const makeChip = async (name, action, xLeft) => {
		const container = new PIXI.Container();
		const sprite = await spriteFrom(`/gui/buttons/${
			{ deck: 'draw', crop: 'harvest', meat: 'devour' }[name]
		}${mirrored ? '_plain' : ''}.png`);
		if (sprite) {
			sprite.width = bw;
			sprite.height = B;
			container.addChild(sprite);
		}
		const style = {
			fontFamily: 'Luckiest Guy', fontSize, fill: 0xffffff,
			stroke: { color: 0x000000, width: 3 },
		};
		if (name === 'deck') {
			// "hand/deck" — THREE texts so the HAND number can flash green on a
			// draw and the DECK number can run the low-deck color ramp while the
			// slash between them stays plain white (updateBarSide recenters the
			// trio on update).
			const label = new PIXI.Container();
			label.x = bw / 2;
			label.y = B * 0.55;
			const handPart = new PIXI.Text({ text: '', style: { ...style } });
			const slashPart = new PIXI.Text({ text: '/', style: { ...style } });
			const deckPart = new PIXI.Text({ text: '', style: { ...style } });
			handPart.anchor.set(0, 0.5);
			slashPart.anchor.set(0, 0.5);
			deckPart.anchor.set(0, 0.5);
			label.addChild(handPart, slashPart, deckPart);
			container.addChild(label);
			counts.deckHand = handPart;
			counts.deckSlash = slashPart;
			counts.deck = deckPart;
		} else {
			const count = new PIXI.Text({ text: '', style });
			count.anchor.set(0.5);
			count.x = bw / 2;
			count.y = B * 0.55;
			container.addChild(count);
			counts[name] = count;
		}
		// Pivot-centered so the resource-change bounce grows around the middle.
		container.pivot.set(bw / 2, B / 2);
		container.x = xLeft + bw / 2;
		container.y = cy;
		chipRefs[name] = { container, w: bw, h: B };

		// YOUR meat chip carries the persistent Devour targeting ring
		// (restyled in place by applyBarTargeting).
		if (!mirrored && action === 'Devour') {
			const ring = new PIXI.Graphics();
			container.addChildAt(ring, 0);
			refs.devourChip = { container, ring, w: bw, h: B };
		}
		// The Draw chip is where drawn cards fly in from (hand.js).
		if (!mirrored && action === 'Draw') {
			refs.drawChip = container;
		}
		if (!mirrored && interactive && action) {
			container.eventMode = 'static';
			container.cursor = 'pointer';
			container.on('pointertap', () => click(action));
		}
		layer.addChild(container);
	};

	// --- Left side: [clock][deck] as a HUDDLED pair, pulled toward CENTER -----
	// The clock (tick counter) hugs the Draw button, and the pair sits close to
	// the portrait, with the leftover slack banked at the FRAME EDGE — so the
	// clock/Draw read as a cluster near the middle instead of floating out by
	// the frame. (leftInner = clock↔Draw, leftPortraitPad = Draw↔portrait.)
	const leftSlack = Math.max(3 * MIN_GAP, leftAvail - 2 * bw);
	const leftInner = leftSlack * 0.16;
	const leftPortraitPad = leftSlack * 0.22;
	const leftPad = leftSlack - leftInner - leftPortraitPad; // banked at the frame edge
	let clock = null;
	const clockSprite = await spriteFrom('/gui/clock/0-4.png');
	// All 9 frames preload so the pulse-refill can swap textures synchronously.
	void Promise.all(Array.from({ length: 9 }, (_, n) => PIXI.Assets.load(`/gui/clock/${n}-4.png`).catch(() => {})));
	let tickCount = null;
	if (clockSprite) {
		clock = clockSprite;
		clock.anchor.set(0.5);
		clock.width = clockH;
		clock.height = clockH;
		clock.scale.x = -Math.abs(clock.scale.x); // mirrored like the original
		clock.x = leftEdge + leftPad + bw / 2; // centered in its slot
		clock.y = cy;
		clock.__baseScale = { x: clock.scale.x, y: clock.scale.y };
		layer.addChild(clock);
		// The 5+ SURPLUS counter: the dial's frames stop at 8, so a big tick
		// pile just reads "full clock" — the number over the dial carries the
		// REAL count, colored along the wheel (see tickCountHue). Hidden < 5.
		tickCount = new PIXI.Text({
			text: '',
			style: {
				fontFamily: 'Luckiest Guy', fontSize: Math.max(12, clockH * 0.52),
				fill: 0xffffff, stroke: { color: 0x000000, width: 3 },
			},
		});
		tickCount.anchor.set(0.5);
		tickCount.x = clock.x;
		tickCount.y = clock.y;
		tickCount.visible = false;
		layer.addChild(tickCount);
	}
	await makeChip('deck', 'Draw', leftEdge + leftPad + bw + leftInner);

	// --- Center: the portrait (fills the frame's hole) ----------------------
	await buildPortrait(scene, layer, { heroIndex, mirrored, refs, portraitD });

	// --- Right side: [crop][meat], same huddled-toward-center pair -----------
	// Harvest hugs the portrait; Devour (meat) hugs Harvest; slack banks at the
	// frame edge — mirror of the left so the meat/Devour sits near the middle.
	const rightSlack = Math.max(3 * MIN_GAP, rightAvail - 2 * bw);
	const rightInner = rightSlack * 0.16;
	const rightPortraitPad = rightSlack * 0.22;
	const rightStart = centerX + portraitD / 2;
	await makeChip('crop', 'Crop', rightStart + rightPortraitPad);
	await makeChip('meat', 'Devour', rightStart + rightPortraitPad + bw + rightInner);

	return { clock, tickCount, counts, chips: chipRefs, layer, tick: null, realTick: null, prev: null, deckFlash: null };
}

/**
 * The leader-portrait chip: the face target ("Yourself" on your bar,
 * "Opponent" on theirs). A CIRCLE, positioned to fill the duel frame's
 * portrait hole (metrics.portraitTop/portraitBot). Tap targets it when it's
 * actionable; press-hold shows the full leader card (same hold-to-view as
 * cards). applyBarTargeting restyles its ring in place.
 * @param {object} scene
 * @param {PIXI.Container} layer
 * @param {{heroIndex:number, mirrored:boolean, refs:any, portraitD:number}} opts
 * @returns {Promise<number>}  The width consumed (advance for the row's x).
 */
async function buildPortrait(scene, layer, { heroIndex, mirrored, refs, portraitD }) {
	const spot = mirrored ? 'Opponent' : 'Yourself';
	const size = portraitD;
	const radius = size / 2; // a circle, sized to cover the frame's hole
	const hole = mirrored ? scene.metrics.portraitTop : scene.metrics.portraitBot;
	const container = new PIXI.Container();
	container.x = (hole?.x ?? 0) - size / 2;
	container.y = (hole?.y ?? 0) - size / 2;

	// The FACE group (backing + art + circular mask), center-pivoted so a
	// direct hit can TILT it inside the frame (animations knockbackPortrait).
	// The targeting ring stays on the outer container — UI chrome, not a
	// physical part of the leader, so it doesn't wobble with the blow.
	const face = new PIXI.Container();
	face.position.set(radius, radius);
	face.pivot.set(radius, radius);
	container.addChild(face);
	// A dark backing circle so a slow/missing face never shows a hole.
	face.addChild(new PIXI.Graphics().circle(radius, radius, radius).fill(0x141119));
	const sprite = await spriteFrom(`/leaders/faces/${HERO_ASSET_NAMES[heroIndex] || 'john'}.png`);
	if (sprite) {
		sprite.width = size;
		sprite.height = size;
		const cornerMask = new PIXI.Graphics().circle(radius, radius, radius).fill(0xffffff);
		face.addChild(sprite, cornerMask);
		sprite.mask = cornerMask;
	}

	// Highlight ring ABOVE the art (same language as board tiles), restyled
	// in place by applyBarTargeting.
	const ring = new PIXI.Graphics();
	container.addChild(ring);

	container.eventMode = 'static';
	container.cursor = 'pointer';
	container.on('pointerdown', () => scene.beginLeaderZoom(heroIndex));
	container.on('pointerup', () => {
		if (scene.endZoom()) return;      // the hold-to-view consumed this press
		if (highlightFor(spot)) click(spot);
	});
	container.on('pointerupoutside', () => scene.cancelZoom());

	layer.addChild(container);
	refs.portraits[spot] = { container, face, sprite, ring, w: size, h: size, r: radius };
	return size + 8;
}

/**
 * A plain purple text button (Mulligan).
 * @param {string} label
 * @param {number} x        Center x — or the LEFT edge when leftAligned.
 * @param {number} y        Top y.
 * @param {() => void} onTap
 * @param {boolean} [leftAligned]
 * @returns {PIXI.Container}
 */
function textButton(label, x, y, onTap, leftAligned = false) {
	const container = new PIXI.Container();
	const text = new PIXI.Text({
		text: label,
		style: { fontFamily: 'Luckiest Guy', fontSize: 14, fill: 0xffffff },
	});
	const bg = new PIXI.Graphics()
		.roundRect(0, 0, text.width + 20, text.height + 10, 6)
		.fill(0x5c54a4);
	text.x = 10;
	text.y = 5;
	container.addChild(bg, text);
	container.x = leftAligned ? x : x - container.width / 2;
	container.y = y;
	container.eventMode = 'static';
	container.cursor = 'pointer';
	container.on('pointertap', onTap);
	return container;
}

/**
 * Load a sprite from a URL via the PIXI assets cache (never throws).
 * @param {string} url
 * @returns {Promise<PIXI.Sprite|null>}
 */
async function spriteFrom(url) {
	try {
		const texture = await PIXI.Assets.load(url);
		return new PIXI.Sprite(texture);
	} catch {
		return null;
	}
}
